import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { homePath } from "@/lib/nav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Radio } from "lucide-react";
import { toast } from "sonner";

export function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const u = await login(email, password);
      navigate(homePath(u));
    } catch (e) {
      setErr(e.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Welcome back" sub="Sign in to your VYOMRA CRM account">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <Label>Email</Label>
          <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" placeholder="you@company.com" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label>Password</Label>
            <Link to="/forgot-password" className="text-xs text-blue-700 hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
          </div>
          <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" placeholder="••••••••" />
        </div>
        {err && <p className="text-sm text-rose-600" data-testid="login-error">{err}</p>}
        <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-[#1e3a8a] hover:bg-[#1e40af]">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign in
        </Button>
        <p className="text-center text-sm text-slate-500">
          New here? <Link to="/register" className="text-blue-700 font-medium hover:underline" data-testid="go-register">Create a business account</Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function Signup() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ business_name: "", name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/register", f);
      setUser(data);
      toast.success(`Welcome, ${data.business_name}!`);
      navigate(homePath(data));
    } catch (e) {
      setErr(apiError(e, "Could not create account"));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Start your business" sub="Create your workspace on VYOMRA CRM">
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div>
          <Label>Business Name</Label>
          <Input data-testid="signup-business" value={f.business_name} onChange={(e) => set("business_name", e.target.value)} required className="mt-1.5" placeholder="e.g. Gujarat Sanchar" />
        </div>
        <div>
          <Label>Your Name</Label>
          <Input data-testid="signup-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" placeholder="Full name" />
        </div>
        <div>
          <Label>Email</Label>
          <Input data-testid="signup-email" type="email" value={f.email} onChange={(e) => set("email", e.target.value)} required className="mt-1.5" placeholder="you@company.com" />
        </div>
        <div>
          <Label>Password</Label>
          <Input data-testid="signup-password" type="password" value={f.password} onChange={(e) => set("password", e.target.value)} required minLength={6} className="mt-1.5" placeholder="Create a password" />
        </div>
        {err && <p className="text-sm text-rose-600" data-testid="signup-error">{err}</p>}
        <Button type="submit" disabled={loading} data-testid="signup-submit" className="w-full bg-[#1e3a8a] hover:bg-[#1e40af]">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Create business account
        </Button>
        <p className="text-center text-sm text-slate-500">
          Already have an account? <Link to="/login" className="text-blue-700 font-medium hover:underline" data-testid="go-login">Sign in</Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/auth/forgot-password", { email }); } catch {}
    setSent(true); setLoading(false);
  };
  return (
    <AuthShell title="Reset password" sub="We'll email you a reset link">
      {sent ? (
        <div className="space-y-4">
          <p className="text-sm text-slate-600">If that email is registered, a reset link has been sent. Please check your inbox.</p>
          <Link to="/login" className="text-sm text-blue-700 hover:underline">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Email</Label>
            <Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" />
          </div>
          <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-[#1e3a8a] hover:bg-[#1e40af]">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Send reset link
          </Button>
          <Link to="/login" className="block text-center text-sm text-blue-700 hover:underline">Back to sign in</Link>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (e) {
      toast.error(apiError(e, "Invalid or expired reset link"));
    } finally { setLoading(false); }
  };
  return (
    <AuthShell title="Set new password" sub="Enter a new password for your account">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>New password</Label>
          <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="mt-1.5" />
        </div>
        <Button type="submit" disabled={loading || !token} data-testid="reset-submit" className="w-full bg-[#1e3a8a] hover:bg-[#1e40af]">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update password
        </Button>
        <Link to="/login" className="block text-center text-sm text-blue-700 hover:underline">Back to sign in</Link>
      </form>
    </AuthShell>
  );
}

function AuthShell({ title, sub, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0f172a] p-12 text-white">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-blue-600 grid place-items-center font-bold font-display">V</div>
          <div>
            <div className="font-bold font-display text-lg leading-none">VYOMRA CRM</div>
            <div className="text-xs text-slate-400 mt-1">Order Management Platform</div>
          </div>
        </div>
        <div>
          <h1 className="text-4xl font-bold font-display leading-tight">Run your agency,<br />done simply.</h1>
          <p className="mt-4 text-slate-300 max-w-md">Give every business its own branded workspace to track campaigns, payments and progress. Your customers always know what they ordered, what's happening and how much is paid.</p>
          <div className="mt-8 flex items-center gap-2 text-slate-400 text-sm">
            <Radio className="h-4 w-4 text-blue-400" /> Lead Generation • WhatsApp Promotion • Meta Ads • Social Media
          </div>
        </div>
        <p className="text-xs text-slate-500">© {new Date().getFullYear()} VYOMRA CRM</p>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="h-9 w-9 rounded-lg bg-blue-600 grid place-items-center text-white font-bold font-display">V</div>
            <div className="font-bold font-display text-lg">VYOMRA CRM</div>
          </div>
          <h2 className="text-2xl font-bold font-display text-slate-900">{title}</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">{sub}</p>
          {children}
        </div>
      </div>
    </div>
  );
}
